import React from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ListGroup,
  ListGroupItem,
  ModalFooter,
  ModalProps,
  Button,
} from 'reactstrap';
import { Pagination, PaginationContent } from '@availity/pagination';
import { PaginationControls, DebouncedSearch } from '@/components';
import { FaUser } from 'react-icons/fa';
import { Form, CheckboxGroup, Checkbox } from '@availity/form';
import { useMutation, useQuery, useApolloClient } from '@apollo/react-hooks';
import { agentPagination } from '@/graphql/queries';
import { updateAgents } from '@/graphql/mutations';
import { useUser, useNotifications } from '@/hooks';
import useProfile from '@/areas/Profile/useProfile';

const roleText = (role: string): string => {
  switch (role) {
    case 'user':
      return 'User';
    case 'manager':
      return 'Manager';
    case 'admin':
      return 'Administrator';
    case 'super_admin':
      return 'Super Admin';
    default:
      return 'User';
  }
};

const ProfileEditAgentsModal: React.SFC<Omit<ModalProps, 'toggle'>> = ({
  isOpen,
  toggle,
}) => {
  const client = useApolloClient();
  const { data: user } = useProfile();
  const { create: createNotification } = useNotifications();
  const [updateUsers, { loading: updating }] = useMutation(updateAgents, {
    onCompleted: () => {
      createNotification({
        message: 'Successfully update Agents.',
        type: 'alert',
        action: 'MESSAGE',
        color: 'success',
      });

      toggle();
    },
    onError: () => {
      createNotification({
        message: 'Failed to update Agents.',
        type: 'alert',
        action: 'MESSAGE',
        color: 'danger',
      });
    },
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <Form
        onSubmit={(values, formikBag) => {
          updateUsers({
            variables: {
              input: {
                managerID: user.agentID,
                agentIDs: values.agents,
              },
            },
            update(cache, fetchResult) {
              // Update the agents list response
              cache.writeQuery({
                query: agentPagination,
                data: {
                  agentPagination: {
                    items: fetchResult.data.agentsUpdate,
                  },
                },
                variables: {
                  filter: {
                    agentID: user.agentID,
                    q: '',
                  },
                  orderBy: {
                    firstName: 'ASC',
                  },
                },
              });

              // Reset the form with the new agentIDs
              formikBag.resetForm({
                values: {
                  searchText: '',
                  agents: fetchResult.data.agentsUpdate.map(
                    (a: any) => a.agentID
                  ),
                },
              });
            },
          });
        }}
        initialValues={{
          agents: user.agentIDs,
          searchText: '',
        }}
      >
        {({ values }) => {
          const fetchItems = async (
            currentPage: number,
            itemsPerPage: number
          ): Promise<any> => {
            const response = await client.query({
              query: agentPagination,
              variables: {
                filter: {
                  network: true,
                  q: values.searchText,
                },
                perPage: itemsPerPage,
                page: currentPage,
                orderBy: {
                  firstName: 'ASC',
                },
              },
            });

            const currentPageData = response.data?.agentPagination.items;

            return {
              totalCount: response.data.agentPagination.count,
              items: currentPageData,
            };
          };

          return (
            <>
              <ModalHeader toggle={toggle}>Network Agents</ModalHeader>
              <Pagination
                items={fetchItems}
                itemsPerPage={10}
                resetParams={[values.searchText]}
              >
                <ModalBody>
                  <div className="d-flex">
                    <DebouncedSearch name="searchText" />
                    <PaginationControls
                      pageRange={3}
                      marginPages={0}
                      directionLinks
                    />
                  </div>
                  <CheckboxGroup
                    name="agents"
                    for="agents"
                    groupClassName="bg-dark"
                  >
                    <PaginationContent
                      containerProps={{
                        className: 'bg-dark text-white',
                      }}
                      containerTag={ListGroup}
                      itemKey="agentID"
                      component={({ agentID, firstName, lastName, role }) => (
                        <ListGroupItem className="d-flex justify-content-between align-items-center">
                          <Checkbox
                            name={agentID}
                            label={`${firstName} ${lastName} - ${roleText(
                              role
                            )}`}
                            value={agentID}
                          />
                          <FaUser />
                        </ListGroupItem>
                      )}
                    />
                  </CheckboxGroup>
                </ModalBody>
              </Pagination>
              <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={updating}>
                  Save Agents
                </Button>
              </ModalFooter>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export default ProfileEditAgentsModal;
